"use client";

import { IBaseLabelValue } from "@/interfaces";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import React, { useRef, useState } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedGasBillHistoryInquiryResData } from "@/interfaces/inquiries/governmental";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { usePayFinesMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { useAppStore } from "@/store";

const GasBillHistoryInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedGasBillHistoryInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const { data: session } = useSession();
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const router = useRouter();
  const response = data.data.data;
  if (!response) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          برای این شناسه قبضی صادر نشده
        </div>
      </div>
    );
  }
  const [openAccordionId, setOpenAccordionId] = useState<number>(0);
  const { bill_id } = data.data.input;
  const formatPrice = (value?: string | number) =>
    value ? `${formatNumberWithDots(Number(value) / 10)} تومان` : "-";
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.inputLabel"),
      value: bill_id || "",
    },
  ];
  const billItemResultContent: IBaseLabelValue[] = [
    {
      label: t("gasBill.ownerName"),
      value: "ownerName",
    },
    {
      label: t("gasBill.useType"),
      value: "useType",
    },
    {
      label: t("gasBill.subscriptionNumber"),
      value: "subscriptionNumber",
    },
    {
      label: t("gasBill.addressCode"),
      value: "addressCode",
    },
    {
      label: t("gasBill.city"),
      value: "city",
    },
    {
      label: t("gasBill.areaCode"),
      value: "areaCode",
    },
    {
      label: t("gasBill.meterSerial"),
      value: "meterSerial",
    },
    {
      label: t("gasBill.fileNumber"),
      value: "fileNumber",
    },
    {
      label: t("gasBill.unitCount"),
      value: "unitCount",
    },
    {
      label: t("gasBill.group"),
      value: "group",
    },
    {
      label: t("gasBill.capacity"),
      value: "capacity",
    },
    {
      label: t("gasBill.currentReadingDate"),
      value: "currentReadingDate",
    },
    {
      label: t("gasBill.previousReadingDate"),
      value: "previousReadingDate",
    },
    {
      label: t("gasBill.currentCounterDigit"),
      value: "currentCounterDigit",
    },
    {
      label: t("gasBill.previousCounterDigit"),
      value: "previousCounterDigit",
    },
    {
      label: t("gasBill.counterActivity"),
      value: "counterActivity",
    },
    {
      label: t("gasBill.standardConsumption"),
      value: "standardConsumption",
    },
    {
      label: t("gasBill.gasConsumptionAmount"),
      value: "gasConsumptionAmount",
    },
    {
      label: t("gasBill.subscription"),
      value: "subscription",
    },
    {
      label: t("gasBill.taxes"),
      value: "taxes",
      type: "price",
    },
    {
      label: t("gasBill.insurance"),
      value: "insurance",
    },
    {
      label: t("gasBill.otherDebt"),
      value: "otherDebt",
      type: "price",
    },
    {
      label: t("gasBill.remainingDebt"),
      value: "remainingDebt",
      type: "price",
    },
    {
      label: t("gasBill.remainingPreviousInvoice"),
      value: "remainingPreviousInvoice",
      type: "price",
    },
    {
      label: t("gasBill.debtCount"),
      value: "debtCount",
    },
    {
      label: t("gasBill.serialNumber"),
      value: "serialNumber",
    },
    {
      label: t("gasBill.remainingAmount1000Rial"),
      value: "remainingAmount1000Rial",
      type: "price",
    },
    {
      label: t("gasBill.deductionAmount1000Rial"),
      value: "deductionAmount1000Rial",
      type: "price",
    },
    {
      label: t("gasBill.villageGasTaxes"),
      value: "villageGasTaxes",
      type: "price",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  // const handlePayBillSubmission = () => {
  //   const payload: IPayFinesPayload = {
  //     amount: response.amount,
  //     callback_url: generatePaymentCallbackUrl(pathName),
  //     bill_id: Number(response.billID),
  //     pay_id: Number(response.payID),
  //   };
  //   PayFineFn(
  //     { payload },
  //     {
  //       onSuccess: (data) => {
  //         if (data.data) {
  //           setPreviousPath(pathName);
  //           window.location.href = data.data?.payment_link;
  //         }
  //       },
  //     },
  //   );
  // };
  const buildPdfTableData = () => {
    const tableRows = [];

    tableRows.push(["", ""]);

    response.bills.forEach((bill, idx) => {
      tableRows.push([`قبض شماره ${idx + 1}`, ""]);

      billItemResultContent.forEach((item) => {
        tableRows.push([
          item.label,
          getBillValue(bill.info, item.value, item.type),
        ]);
      });

      tableRows.push(["", ""]);
    });

    return tableRows;
  };

  const getBillValue = (item: any, key: string | number, type?: string) => {
    if (type === "price") {
      return formatPrice(item[key]);
    } else {
      return item[key] ?? "-";
    }
  };
  return (
    <div className="shadow-main relative basis-[500px] rounded-2xl bg-linear-to-b from-rose-500 to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          استعلام تاریخچه قبوض گاز
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("postalCodeInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="gas-inquiry-download-pdf"
            resultContent={buildPdfTableData()}
            tableTitle="تاریخچه قبوض گاز"
            fileName={`تاریخچه قبض گاز - ${bill_id}`}
          />
        </FlexBetween>
        <div className="mb-3 flex items-center text-sm md:text-base">
          <span>تعداد کل قبوض :</span>
          <span className="mx-1">{response.bills.length} عدد</span>
        </div>
        <div className="max-h-[500px] overflow-x-hidden overflow-y-auto">
          {response.bills.map((billItem, i) => (
            <Accordion
              key={i}
              title={`${formatNumberWithDots(Number(billItem.amount) / 10)} تومان `}
              open={i === openAccordionId}
              hasDetails
              className="mb-2"
              onOpenChange={(isOpen) => {
                setOpenAccordionId(isOpen ? i : 0);
              }}
              details={
                <>
                  <p className="text-sm">مهلت پرداخت</p>
                  <p className="text-sm" dir="ltr">
                    {billItem.date}
                  </p>
                </>
              }
            >
              <div
                className={cn(
                  "shadow-main mb-10 flex flex-col gap-4 rounded-xl p-4",
                )}
              >
                <ul className="flex flex-col gap-4">
                  {billItemResultContent.map((item, index) => (
                    <li
                      key={index}
                      className="flex-between flex items-start gap-6 text-xs md:gap-8 md:text-sm"
                    >
                      <span className="min-w-max">{item.label}</span>
                      <div className="text-primary-main flex items-center gap-2 text-start">
                        {getBillValue(billItem.info, item.value, item.type)}
                      </div>
                    </li>
                  ))}
                </ul>
                {/*{warning.finalPrice !== "0" && (*/}
                {/*  <CustomButton*/}
                {/*    onClick={() =>*/}
                {/*      handlePayFineSubmission({*/}
                {/*        paperId: Number(warning.paperId),*/}
                {/*        paymentId: Number(warning.paymentId),*/}
                {/*        inquirePrice: warning.finalPrice,*/}
                {/*      })*/}
                {/*    }*/}
                {/*    variant="outline"*/}
                {/*    className={cn(*/}
                {/*      "text-success-main border-success-main hover:shadow-main hover:text-success-main mx-auto w-max px-2",*/}
                {/*    )}*/}
                {/*  >*/}
                {/*      <span className="text-sm">*/}
                {/*        {t("fines.warningDTOs.payThisFine")}*/}
                {/*      </span>*/}
                {/*  </CustomButton>*/}
                {/*)}*/}
              </div>
            </Accordion>
          ))}
        </div>
        <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="gas-inquiry-download-pdf"
            resultContent={buildPdfTableData()}
            tableTitle="تاریخچه قبوض گاز"
            fileName={`تاریخچه قبض گاز - ${bill_id}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-gas-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-gas-inquiry-new-request"
              onClick={() =>
                router.push(
                  routes.services.Gas_Bill_History_inquiry.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="تاریخچه قبوض گاز"
          resultContent={buildPdfTableData()}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default GasBillHistoryInquiryDetails;
